import React, { useCallback, useState } from 'react';
import { useDropzone, type FileRejection } from 'react-dropzone';
import toast from 'react-hot-toast';
import { FileText } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { ReactComponent as DragAndDropIcon } from '@/assets/icons/drag-and-drop.svg';
import { ReactComponent as XCircleIcon } from '@/assets/icons/x-circle.svg';

const MAX_TOTAL_UPLOAD_BYTES = 5 * 1024 * 1024; // 5MB total
const MAX_TOTAL_UPLOAD_LABEL = '5MB';
const MAX_FILES = 10;
const ACCEPTED_FORMATS_LABEL = 'png, jpg, pdf, docx, xlsx';
const FILE_SIZE_ERROR_MESSAGE = `Total upload size must be ${MAX_TOTAL_UPLOAD_LABEL} or smaller.`;

const ACCEPT_OBJECT: Record<string, string[]> = {
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'application/pdf': ['.pdf'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx']
};

interface CreateWorkDocumentStepProps {
  files: File[];
  onFilesChange: (files: File[]) => void;
  existingUrls?: string[];
  onExistingUrlsChange?: (urls: string[]) => void;
}

const isImageFile = (file: File) => file.type.startsWith('image/');

const isImageUrl = (url: string) => /\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/i.test(url);

const getFileNameFromUrl = (url: string) => {
  try {
    const pathname = new URL(url).pathname;
    return decodeURIComponent(pathname.split('/').pop() || 'Document');
  } catch {
    return url.split('/').pop() || 'Document';
  }
};

const formatFileSize = (bytes: number) =>
  bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(2)} MB` : `${(bytes / 1024).toFixed(2)} KB`;

const getTotalFileSize = (fileList: File[]) => fileList.reduce((total, file) => total + file.size, 0);

const CreateWorkDocumentStep: React.FC<CreateWorkDocumentStepProps> = ({
  files,
  onFilesChange,
  existingUrls = [],
  onExistingUrlsChange
}) => {
  const [uploadError, setUploadError] = useState<string | null>(null);
  const totalDocuments = files.length + existingUrls.length;

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const remainingSlots = Math.max(0, MAX_FILES - existingUrls.length - files.length);
      const limitedAccepted = acceptedFiles.slice(0, remainingSlots);
      const merged = [...files, ...limitedAccepted];
      const totalSize = getTotalFileSize(merged);

      if (totalSize > MAX_TOTAL_UPLOAD_BYTES) {
        setUploadError(FILE_SIZE_ERROR_MESSAGE);
        toast.error(`Total upload size cannot exceed ${MAX_TOTAL_UPLOAD_LABEL}. Please upload smaller files.`);
        return;
      }

      setUploadError(null);
      onFilesChange(merged);
    },
    [existingUrls.length, files, onFilesChange]
  );

  const onDropRejected = useCallback((fileRejections: FileRejection[]) => {
    const typeRejection = fileRejections.find((rejection) =>
      rejection.errors.some((error) => error.code === 'file-invalid-type')
    );

    if (typeRejection) {
      setUploadError(null);
      toast.error(`File "${typeRejection.file.name}" is not a supported document type.`);
    }
  }, []);

  const removeFile = useCallback(
    (indexToRemove: number) => {
      setUploadError(null);
      onFilesChange(files.filter((_, index) => index !== indexToRemove));
    },
    [files, onFilesChange]
  );

  const removeExistingUrl = useCallback(
    (indexToRemove: number) => {
      if (!onExistingUrlsChange) {
        return;
      }
      setUploadError(null);
      onExistingUrlsChange(existingUrls.filter((_, index) => index !== indexToRemove));
    },
    [existingUrls, onExistingUrlsChange]
  );

  const { getRootProps, getInputProps, isDragActive, isDragReject, open } = useDropzone({
    onDrop,
    onDropRejected,
    accept: ACCEPT_OBJECT,
    maxFiles: MAX_FILES,
    multiple: true,
    noClick: true,
    noKeyboard: true,
    disabled: totalDocuments >= MAX_FILES
  });

  return (
    <div className="flex-1">
      <p className="font-bold text-lg">Upload Supporting Document</p>
      <p className="text-sm text-secondary mt-1">Show evidence of what you're working on</p>

      <div className="mt-6">
        <label className="text-sm font-medium leading-[100%] inline-block mb-2">Upload a File</label>

        <div
          {...getRootProps()}
          className={`
            flex flex-col items-center justify-center gap-4 border border-dashed rounded-[16px] py-10 px-4 cursor-pointer transition-colors
            ${uploadError ? 'border-red-500 bg-red-50' : isDragActive ? 'border-primary bg-primary/5' : 'border-gray-300'}
            ${isDragReject ? 'border-red-500 bg-red-50' : ''}
          `}
        >
          <input {...getInputProps()} />

          <DragAndDropIcon />

          <div className="text-center">
            <p className="font-semibold">
              {isDragActive
                ? isDragReject
                  ? 'File type not supported'
                  : 'Drop the files here'
                : 'Drag and Drop Documents here'}
            </p>
            <p className="text-xs text-secondary mt-1">
              Accepted document {ACCEPTED_FORMATS_LABEL}. max {MAX_TOTAL_UPLOAD_LABEL.toLowerCase()} total
            </p>
          </div>

          <Button
            type="button"
            variant="primary-reverse"
            className="rounded-full font-semibold px-6"
            onClick={(e) => {
              e.stopPropagation();
              open();
            }}
            disabled={totalDocuments >= MAX_FILES}
          >
            Upload from Computer
          </Button>
        </div>

        {uploadError && <p className="mt-2 text-xs text-red-600">{uploadError}</p>}
      </div>

      {(existingUrls.length > 0 || files.length > 0) && (
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          {existingUrls.map((url, index) => (
            <div key={`${url}-${index}`} className="flex items-center justify-between gap-3 border rounded-[16px] p-4">
              <div className="flex items-center gap-3 min-w-0">
                {isImageUrl(url) ? (
                  <img
                    className="w-12 h-12 rounded-[8px] object-cover shrink-0"
                    src={url}
                    alt={getFileNameFromUrl(url)}
                  />
                ) : (
                  <div className="w-12 h-12 rounded-[8px] bg-lightPurple flex items-center justify-center shrink-0">
                    <FileText className="w-6 h-6 text-primary" />
                  </div>
                )}
                <div className="min-w-0">
                  <p title={getFileNameFromUrl(url)} className="text-sm font-semibold truncate">
                    {getFileNameFromUrl(url)}
                  </p>
                  <span className="text-xs text-primary font-medium">Existing document</span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => removeExistingUrl(index)}
                className="hover:scale-110 transition-transform shrink-0"
                title="Remove file"
              >
                <XCircleIcon />
              </button>
            </div>
          ))}

          {files.map((file, index) => (
            <div
              key={`${file.name}-${index}`}
              className="flex items-center justify-between gap-3 border rounded-[16px] p-4"
            >
              <div className="flex items-center gap-3 min-w-0">
                {isImageFile(file) ? (
                  <img
                    className="w-12 h-12 rounded-[8px] object-cover shrink-0"
                    src={URL.createObjectURL(file)}
                    alt={file.name}
                  />
                ) : (
                  <div className="w-12 h-12 rounded-[8px] bg-lightPurple flex items-center justify-center shrink-0">
                    <FileText className="w-6 h-6 text-primary" />
                  </div>
                )}
                <div className="min-w-0">
                  <p title={file.name} className="text-sm font-semibold truncate">
                    {file.name}
                  </p>
                  <span className="text-xs text-primary font-medium">{formatFileSize(file.size)}</span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => removeFile(index)}
                className="hover:scale-110 transition-transform shrink-0"
                title="Remove file"
              >
                <XCircleIcon />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CreateWorkDocumentStep;
